/**
* @module Table
* @description A reusable table component with fixed layout, scrolling, and styled borders.
*/
import { sum } from 'lodash';
import styled from 'styled-components';

/**
* @function Table
* @description Renders a styled table with configurable column widths and content.
* 
* @param {Object} props - Component props
* @param {Array<number>} props.widths - Array of column widths in percentages
* @param {Array<string>} props.headers - Array of column header titles
* @param {Array<Object>} props.content - Array of data objects to display
* 
* @returns {React.Element} Scrollable table component
* 
* @description
* Features:
* - Fixed header with scrollable body
* - Configurable column widths
* - Alternating row colors
* - Special handling for product links
* - Left-aligned description columns
* - Styled borders and spacing
* 
* @example
* const tableProps = {
*   widths: [30, 40, 30],
*   headers: ['Item', 'Description', 'Quantity'],
*   content: [
*     {
*       Item: 'Speaker',
*       Description: 'Wall mounted speaker',
*       Quantity: 2,
*       product: { name: 'SP100', link: 'https://example.com' }
*     }
*   ]
* };
* <Table {...tableProps} />
*/
const Table = ({ widths, headers, content }) => {
  const adjustedWidths = widths.slice();
  adjustedWidths.length = headers.length;
  const totalWidth = sum(adjustedWidths);

  return (
    <Table.Scroll>
      <Table.Body width={totalWidth}>
        <colgroup>
          {adjustedWidths.map((e, i) => <col key={`w-${i}`} style={{ width: `${e}%` }} />)}
        </colgroup>
        <tbody>
          <tr>
            {headers.map((title, index) => <th key={`title-${index}`} >{title}</th>)}
          </tr>
          {content.map((item, index) => (
            <tr key={`spkr-${index}`}>
              {headers.map((header, index) => (
                <td key={`td-${index}`} className={['product', 'description'].includes(header) ? 'text-start' : ''}>
                  {header === 'product' ? item[header]?.link !== '#' ?
                    <a href={item[header].link} target="_blank" rel="noopener noreferrer">{item[header].name}</a>
                    : item[header].name
                    : item[header]
                  }
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </Table.Body>
    </Table.Scroll>
  )
};

/**
* @component Table.Scroll
* @description Wrapper component providing scrollable container with max height.
*/
Table.Scroll = styled.div.attrs(props => ({
  className: 'overflow-auto'
}))`
max-height: 530px; 
`;

/**
* @component Table.Body
* @description Styled table component with fixed layout and custom styling.
* 
* Styling features:
* - Fixed table layout
* - Centered text alignment
* - Sticky headers
* - Bordered cells
* - Alternating row backgrounds
* - Custom link styling
*/
Table.Body = styled.table.attrs(props => ({
}))`
    table-layout: fixed;
    position: relative;
    width: ${props => `${props?.width ?? 100}%`};
    font-size: 14px;
    text-align: center;
    border-collapse: separate;
    border-spacing: 0;
    td, th {
      border-right: 1px solid var(--app-medium-gray);
      border-bottom: 1px solid var(--app-medium-gray);
      padding: 4px;
    }
    th {
      font-size: 16px;
      text-transform: uppercase;
      white-space: pre;
      background: #fff;
      position: sticky;
      top: 0
    }
    th:last-child, tr td:last-child {
      border-right: 0px
    }
    tr {
      :nth-of-type(even) {
        background-color: var(--app-light-gray)
      }
    }
    tr:last-child { 
      td {
      border-bottom: 0px
      }
    }
    a {
      color: var(--app-red);
      text-decoration: none
    }

  `;

export default Table;

